import { useNavigate } from 'react-router-dom';
import { TourStepConfig } from '../components/Tour/tourSteps';

/**
 * Hook to handle navigation between pages during the tour
 * Includes element waiting to ensure target elements are rendered before showing tooltips
 */
export const useTourNavigation = () => {
  const navigate = useNavigate();

  /**
   * Wait for an element to appear in the DOM
   * Uses MutationObserver to watch for DOM changes
   */
  const waitForElement = (selector: string, timeout = 10000): Promise<Element> => {
    return new Promise((resolve, reject) => {
      // Check if element already exists
      const existingElement = document.querySelector(selector);
      if (existingElement) {
        console.log(`[Tour] Element ${selector} found immediately`);
        resolve(existingElement);
        return;
      }

      console.log(`[Tour] Waiting for element ${selector}...`);

      // Set up timeout
      const timeoutId = setTimeout(() => {
        observer.disconnect();
        console.error(`[Tour] Element ${selector} not found within ${timeout}ms`);
        reject(new Error(`Element ${selector} not found within ${timeout}ms`));
      }, timeout);

      // Set up MutationObserver to watch for element
      const observer = new MutationObserver(() => {
        const element = document.querySelector(selector);
        if (element) {
          clearTimeout(timeoutId);
          observer.disconnect();
          console.log(`[Tour] Element ${selector} found after waiting`);
          resolve(element);
        }
      });

      // Start observing
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    });
  };

  /**
   * Navigate to a specific tour step's route and wait for the target element
   */
  const navigateToStep = async (step: TourStepConfig): Promise<void> => {
    console.log(`[Tour] Navigating to ${step.route} for step ${step.stepNumber}`);

    // Navigate to the step's route
    navigate(step.route);

    // Wait for the target element to be available
    try {
      await waitForElement(step.target as string);
      // Add a delay to ensure everything is fully rendered and stable
      await new Promise((resolve) => setTimeout(resolve, 800));
      console.log(`[Tour] Ready to show step ${step.stepNumber}`);
    } catch (error) {
      console.error(`Failed to navigate to step ${step.stepNumber}:`, error);
      throw error;
    }
  };

  return {
    navigateToStep,
    waitForElement,
  };
};
